import type { PixCheckoutContext } from "./pixCheckout.js";
import { applyOfferTokenToParams, loadPersistedCheckoutOfferToken, persistCheckoutOfferToken } from "./checkoutOfferSession.js";

export type StripeCheckoutContext = PixCheckoutContext;

/** Cached Embedded Checkout payload from POST /checkout (avoids a round-trip). */
export interface StripeCheckoutSession {
  clientSecret: string;
  publishableKey: string;
  finalCents: number;
  /** New-customer checkout only: what renews, every what, from when (the screen shows it). */
  renewalCents?: number;
  interval?: "monthly" | "annual";
  firstChargeAt?: string | null;
}

export function saveStripeCheckoutContext(ctx: StripeCheckoutContext): void {
  if (ctx.offerToken) persistCheckoutOfferToken(ctx.offerToken);
  sessionStorage.setItem(`stripe_checkout_ctx_${ctx.orderId}`, JSON.stringify(ctx));
}

export function loadStripeCheckoutContext(orderId: string): StripeCheckoutContext | null {
  const raw = sessionStorage.getItem(`stripe_checkout_ctx_${orderId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StripeCheckoutContext;
    return {
      ...parsed,
      offerToken: parsed.offerToken || loadPersistedCheckoutOfferToken() || undefined,
    };
  } catch {
    return null;
  }
}

export function saveStripeCheckoutSession(orderId: string, session: StripeCheckoutSession): void {
  sessionStorage.setItem(`stripe_checkout_session_${orderId}`, JSON.stringify(session));
}

export function loadStripeCheckoutSession(orderId: string): StripeCheckoutSession | null {
  const raw = sessionStorage.getItem(`stripe_checkout_session_${orderId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StripeCheckoutSession;
    if (!parsed.clientSecret || !parsed.publishableKey) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Restore checkout / integrated with the same plan, email, and payment method. */
export function buildStripeBackUrl(ctx: StripeCheckoutContext): string {
  if (ctx.returnTo === "integrated") {
    const params = new URLSearchParams();
    if (ctx.integratedToken) params.set("token", ctx.integratedToken);
    const q = params.toString();
    return q ? `/integrated?${q}` : "/integrated";
  }
  const params = new URLSearchParams({
    plan: ctx.plan,
    interval: ctx.interval,
    method: "stripe",
    email: ctx.email,
  });
  if (ctx.fullName) params.set("name", ctx.fullName);
  if (ctx.phone) params.set("whatsapp", ctx.phone);
  if (ctx.cpf) params.set("cpf", ctx.cpf.replace(/\D/g, ""));
  if (ctx.salesToken) params.set("sales_token", ctx.salesToken);
  if (ctx.phoneLocked) params.set("phone_locked", "1");
  applyOfferTokenToParams(params, ctx.offerToken ?? loadPersistedCheckoutOfferToken());
  return `/checkout?${params.toString()}`;
}
